import Phaser from 'phaser';

export interface RoundedLabelBgOptions {
  color?: number;
  alpha?: number;
  radius?: number;
  /** Extra width added on the RIGHT of the text (e.g. to tuck an icon inside the pill). */
  padRight?: number;
}

/**
 * Draws a soft, rounded-rectangle background behind a text label and returns the
 * Graphics object. Phaser's built-in `backgroundColor` only renders a hard square,
 * so we draw our own rounded rect instead.
 *
 * The rect is positioned at the label's (x, y) with its offset derived from the
 * label's origin, so it lines up for any origin and can be tweened together with
 * the label (add both to the same tween `targets`).
 */
/**
 * What the pill needs to measure itself against. A Text satisfies it, and so does anything else
 * that knows its own box — the split rule banner (BoardScene) is a Container of three Texts and
 * has to be able to wear the same pill.
 */
export interface LabelBox {
  x: number;
  y: number;
  width: number;
  height: number;
  originX: number;
  originY: number;
  depth: number;
}

/**
 * The corner tile every pill is stretched from, baked once per (radius, colour, alpha).
 *
 * A pill used to be a Graphics holding one `fillRoundedRect`, and a Graphics is re-tessellated on
 * EVERY frame — Phaser 3 caches no geometry, and a rounded rect is four arcs that the renderer
 * walks at ~100 points apiece (`iterStep` 0.01 in GraphicsWebGLRenderer), then triangulates. Two
 * pills on the board — the rule banner and the back button — measured 1.9 ms/frame between them on
 * a desktop dev build, out of a 5.2 ms frame, for two shapes that never change.
 *
 * A nine-slice draws the same pill from a tile barely bigger than its own corners: the corners are
 * copied, the edges and middle stretched, all of it batched with the rest of the sprites and none
 * of it re-tessellated. One tile serves every pill of that colour and radius, at any size.
 */
export function pillTexture(scene: Phaser.Scene, radius: number, color: number, alpha: number): string {
  const key = `pill-${radius}-${color.toString(16)}-${alpha}`;
  if (scene.textures.exists(key)) return key;
  // Two corners plus a 2px middle: everything a nine-slice needs to rebuild the shape at any size.
  const size = radius * 2 + 2;
  const g = scene.make.graphics({ x: 0, y: 0 }, false);
  g.fillStyle(color, alpha);
  g.fillRoundedRect(0, 0, size, size, radius);
  g.generateTexture(key, size, size);
  g.destroy();
  return key;
}

export function addRoundedLabelBg(
  scene: Phaser.Scene,
  label: LabelBox,
  options: RoundedLabelBgOptions = {},
): Phaser.GameObjects.NineSlice {
  const { color = 0x1a1f25, alpha = 1, radius = 14, padRight = 0 } = options;
  const textW = label.width;
  const w = textW + padRight;
  const h = label.height;
  // A corner can never be more than half the pill, or the nine-slice's own corners would overlap.
  // Nothing shipped hits this — the labels are all taller than 2 x their radius — but a pill that
  // silently drew itself inside out would be a horrible thing to debug.
  const r = Math.max(0, Math.min(radius, Math.floor(Math.min(w, h) / 2)));
  const bg = scene.add.nineslice(label.x, label.y, pillTexture(scene, r, color, alpha), undefined, w, h, r, r, r, r);
  // Sits where the Graphics sat: at the label's own (x, y), offset by the label's origin, so the
  // two can be tweened together as one. The offset stays based on the TEXT width so the text keeps
  // its position; the pill just grows to the right by padRight.
  bg.setOrigin((label.originX * textW) / w, label.originY);
  bg.setDepth(label.depth - 1);
  return bg;
}
